import type { MillsheetFieldsFragment } from '@mbfab/client-api'
import type { SectionTypeGroupKey } from '@mbfab/shared'
import {
  isNull,
  isPresent,
  isSome,
  numberUtil,
  resolveSectionTypeGroup,
  SECTION_TYPE_GROUPS,
  sectionTypeGroupLabel,
} from '@mbfab/shared'
import dayjs from 'dayjs'
import { sum, uniq } from 'es-toolkit'
import type ExcelJS from 'exceljs'

import type { ColumnConfig } from '../../order-matching/print/svg/main-row'
import { columnTypeLabel } from '../../order-matching/print/svg/main-row'
import type { Item } from '../../order-matching/print/svg/millsheet-list-svg'

import { NUMERIC_COLUMN_TYPES } from './home-print-columns'

//
// 「使用鋼材一覧表」Excel ダウンロード。
// v1 apps/frontend/client/src/components/millsheet/MillsheetListToExcel/DownloadExcelMillsheetListButton.tsx の移植。
//

const ITEM_SHOULD_LENGTH = 18

const border = {
  top: { style: 'thin' },
  left: { style: 'thin' },
  bottom: { style: 'thin' },
  right: { style: 'thin' },
} as const
const center = { vertical: 'middle', horizontal: 'center', wrapText: true } as const
const leftAlign = { vertical: 'middle', horizontal: 'left' } as const
const rightAlign = { vertical: 'middle', horizontal: 'right' } as const

// 形状別シートのタブ順は Notion 仕様の表の並びに合わせる。OTHER は最後尾
const SHAPE_SHEET_ORDER: SectionTypeGroupKey[] = [
  SECTION_TYPE_GROUPS.PL_FB,
  SECTION_TYPE_GROUPS.HTB,
  SECTION_TYPE_GROUPS.PIPE,
  SECTION_TYPE_GROUPS.SHAPE,
  SECTION_TYPE_GROUPS.H_BEAM,
  SECTION_TYPE_GROUPS.OTHER,
]
const UNSET_GROUP_LABEL = '(未設定)'

// v1 apps/frontend/client/src/components/MillsheetPrintForm/MillsheetListSvgV2/MainRow.tsx の
// columnTypeWidth と同じ値（発注照合専用の3列は home では選択肢に出ないため省略せず残す。
// Record の網羅性チェックにより取りこぼしを防ぐ）
const columnTypeWidth: Record<ColumnConfig['type'], number> = {
  steelType: 12,
  sectionType: 12,
  size: 15,
  thicknessDiameter: 10,
  length: 10,
  quantity: 8,
  weight: 12,
  heatNumber: 12,
  individualNumber: 12,
  no: 12,
  manufacturerName: 18,
  constructionArea: 10,
  member: 10,
  buildingPart: 10,
  memo: 15,
  pageNumber: 15,
  tradingCompany: 12,
  orderNumber: 12,
  innerNo: 12,
  endorsementCompany: 14,
  endorsementDate: 12,
  customer: 14,
  referenceNumber: 14,
  supplierName: 14,
}

const ENDORSEMENT_LEVELS = [1, 2, 3, 4, 5, 6] as const

// Excel の1列。通常は ColumnConfig と同じだが、「すべての裏書情報を含める」では
// 裏書会社・裏書日が階層ごとの列に展開されるため、どの階層のどの項目かを持たせる
type ExcelColumn = ColumnConfig & {
  endorsement?: { level: number; field: 'company' | 'date' }
  // 見出しを columnTypeLabel と変えたい列だけ指定する（呼び出し元ごとの表記差を共有ラベルに持ち込まないため）
  label?: string
}

function excelColumnLabel(column: ExcelColumn): string {
  if (isSome(column.endorsement)) {
    const { level, field } = column.endorsement
    return `${field === 'company' ? '裏書会社' : '裏書日'}${level}`
  }
  return column.label ?? columnTypeLabel[column.type]
}

// 裏書は最大6階層で、1が最新・6が最古。「すべての裏書情報を含める」ON のときは
// 古い順（6→1）に「会社, 日」を並べる。裏書会社・裏書日のどちらかが表示項目に含まれていれば
// その位置（先に現れるほう）に展開し、どちらも含まれていなければ「全列出力」の意図を尊重して末尾に足す
function buildExcelColumns(
  columnConfigs: ColumnConfig[],
  includeAllEndorsements: boolean,
  labelOverrides: Partial<Record<ColumnConfig['type'], string>>,
): ExcelColumn[] {
  const withLabel = (columnConfig: ColumnConfig): ExcelColumn => ({
    ...columnConfig,
    label: labelOverrides[columnConfig.type],
  })
  if (!includeAllEndorsements) {
    return columnConfigs.map(withLabel)
  }
  const isEndorsementColumn = (columnConfig: ColumnConfig) =>
    columnConfig.type === 'endorsementCompany' || columnConfig.type === 'endorsementDate'
  const expanded: ExcelColumn[] = [...ENDORSEMENT_LEVELS].reverse().flatMap((level) => [
    { type: 'endorsementCompany', active: true, endorsement: { level, field: 'company' } },
    { type: 'endorsementDate', active: true, endorsement: { level, field: 'date' } },
  ])
  const insertAt = columnConfigs.findIndex(isEndorsementColumn)
  const others = columnConfigs.filter((columnConfig) => !isEndorsementColumn(columnConfig))
  if (insertAt < 0) {
    return [...others.map(withLabel), ...expanded]
  }
  return [...others.slice(0, insertAt).map(withLabel), ...expanded, ...others.slice(insertAt).map(withLabel)]
}

function formatEndorsementDate(value: string | null | undefined): string {
  if (!isPresent(value)) {
    return ''
  }
  // 日付のみの値。タイムゾーン変換でずれないよう先頭10文字（YYYY-MM-DD）だけを使う
  return dayjs(value.slice(0, 10)).format('YYYY/MM/DD')
}

function buildEndorsementCell(item: Item, endorsement: NonNullable<ExcelColumn['endorsement']>): string {
  const companies = [
    item.endorsementCompany,
    item.endorsementCompany2,
    item.endorsementCompany3,
    item.endorsementCompany4,
    item.endorsementCompany5,
    item.endorsementCompany6,
  ]
  const dates = [
    item.endorsementDate,
    item.endorsementDate2,
    item.endorsementDate3,
    item.endorsementDate4,
    item.endorsementDate5,
    item.endorsementDate6,
  ]
  if (endorsement.field === 'company') {
    return companies[endorsement.level - 1] ?? ''
  }
  return formatEndorsementDate(dates[endorsement.level - 1])
}

function buildItemCells(item: Item, columnConfigs: ExcelColumn[]): Array<string | number | null> {
  return columnConfigs.map((columnConfig) => {
    // 階層指定のある裏書列（「すべての裏書情報を含める」で展開された列）だけ Item の階層配列から引く。
    // 通常の裏書会社・裏書日の列は階層1の値（発注照合の Excel も同じ列を使う）
    if (isSome(columnConfig.endorsement)) {
      return buildEndorsementCell(item, columnConfig.endorsement)
    }
    switch (columnConfig.type) {
      case 'steelType':
        return item.steelType ?? ''
      case 'sectionType':
        return item.sectionType ?? ''
      case 'size':
        return item.size ?? ''
      case 'thicknessDiameter':
        return item.thicknessDiameter ?? ''
      case 'length':
        return item.length ?? ''
      case 'quantity':
        return item.quantity ?? ''
      case 'weight':
        return item.weight ?? ''
      case 'heatNumber':
        return item.heatNumber ?? ''
      case 'individualNumber':
        return item.individualNumber ?? ''
      case 'no':
        return item.no ?? ''
      case 'manufacturerName':
        return item.manufacturerName ?? ''
      case 'constructionArea':
        return item.constructionArea ?? ''
      case 'member':
        return item.member ?? ''
      case 'buildingPart':
        return item.buildingPart ?? ''
      case 'memo':
        return item.memo ?? ''
      case 'pageNumber':
        return item.pageNumber ?? ''
      case 'tradingCompany':
        return item.tradingCompany ?? ''
      case 'orderNumber':
        return item.orderNumber ?? ''
      case 'innerNo':
        return item.innerNo ?? ''
      case 'endorsementCompany':
        return item.endorsementCompany ?? ''
      case 'endorsementDate':
        return formatEndorsementDate(item.endorsementDate)
      // 以下はホームでは選択肢に出さない列（発注照合専用）。型を網羅するためだけに残す
      case 'customer':
        return item.customer ?? ''
      case 'referenceNumber':
        return item.referenceNumber ?? ''
      case 'supplierName':
        return item.supplierName ?? ''
      default: {
        const exhaustiveCheck: never = columnConfig.type
        return exhaustiveCheck
      }
    }
  })
}

function parseThicknessDiameterTuple(td: string): number[] | null {
  const parts = td
    .split(/[x×X*]/)
    .map((s) => s.trim())
    .filter((s) => s !== '')
  if (parts.length === 0) {
    return null
  }
  const nums = parts.map((s) => Number(s))
  if (nums.some((n) => !Number.isFinite(n))) {
    return null
  }
  return nums
}

function compareThicknessDiameterDesc(a: string, b: string): number {
  const aUnset = a === UNSET_GROUP_LABEL
  const bUnset = b === UNSET_GROUP_LABEL
  if (aUnset && bUnset) {
    return 0
  }
  if (aUnset) {
    return 1
  }
  if (bUnset) {
    return -1
  }
  const aTuple = parseThicknessDiameterTuple(a)
  const bTuple = parseThicknessDiameterTuple(b)
  if (isNull(aTuple) && isNull(bTuple)) {
    return a.localeCompare(b)
  }
  if (isNull(aTuple)) {
    return 1
  }
  if (isNull(bTuple)) {
    return -1
  }
  const maxLen = Math.max(aTuple.length, bTuple.length)
  const diff = Array.from({ length: maxLen }, (_, i) => (bTuple[i] ?? 0) - (aTuple[i] ?? 0)).find((d) => d !== 0)
  return diff ?? 0
}

function groupByThicknessDiameter(items: Item[]): Array<[string, Item[]]> {
  const groups: Record<string, Item[]> = {}
  for (const item of items) {
    const trimmed = item.thicknessDiameter?.trim() ?? ''
    const key = trimmed === '' ? UNSET_GROUP_LABEL : trimmed
    const bucket = groups[key] ?? []
    bucket.push(item)
    groups[key] = bucket
  }
  return Object.entries(groups).sort(([a], [b]) => compareThicknessDiameterDesc(a, b))
}

function configurePageSetup(ws: ExcelJS.Worksheet): void {
  ws.pageSetup = {
    paperSize: 9,
    orientation: 'portrait',
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    margins: { left: 0.5, right: 0.5, top: 0.75, bottom: 0.75, header: 0.3, footer: 0.3 },
  }
}

function applyColumnWidths(ws: ExcelJS.Worksheet, columnConfigs: ExcelColumn[]): void {
  const widths = columnConfigs.map((columnConfig) => columnTypeWidth[columnConfig.type])
  ws.columns.forEach((col, i) => {
    col.width = widths[i] ?? 12
  })
}

function applyDataRowStyle(row: ExcelJS.Row, columnConfigs: ExcelColumn[]): void {
  row.eachCell((cell, colNumber) => {
    const columnType = columnConfigs[colNumber - 1]?.type
    cell.alignment = (() => {
      if (isSome(columnType) && NUMERIC_COLUMN_TYPES.includes(columnType)) {
        return rightAlign
      }
      return colNumber === 1 ? center : leftAlign
    })()
    cell.border = border
  })
}

function findColumnIndex(columnConfigs: ExcelColumn[], type: ColumnConfig['type']): number {
  return columnConfigs.findIndex((c) => c.type === type)
}

function sumColumnAsInteger(items: Item[], key: 'quantity' | 'weight'): number {
  return sum(items.map((item) => numberUtil.forceNumber(item[key] ?? 0)).filter((n) => Number.isFinite(n)))
}

function buildAggregationRowCells(items: Item[], columnConfigs: ExcelColumn[], label: string): Array<string | number> {
  const quantityIdx = findColumnIndex(columnConfigs, 'quantity')
  const weightIdx = findColumnIndex(columnConfigs, 'weight')
  const labelIdx = (() => {
    const candidates = [quantityIdx, weightIdx].filter((i) => i >= 0)
    if (candidates.length === 0) {
      return 0
    }
    return Math.max(0, Math.min(...candidates) - 1)
  })()
  const quantitySum = quantityIdx >= 0 ? sumColumnAsInteger(items, 'quantity') : ''
  const weightSum = weightIdx >= 0 ? sumColumnAsInteger(items, 'weight') : ''
  return columnConfigs.map((_, i) => {
    if (i === labelIdx) {
      return label
    }
    if (i === quantityIdx) {
      return quantitySum
    }
    if (i === weightIdx) {
      return weightSum
    }
    return ''
  })
}

function applyAggregationRowStyle(row: ExcelJS.Row, isTotal: boolean): void {
  row.eachCell((cell) => {
    cell.border = border
    cell.font = { bold: true }
    cell.alignment = rightAlign
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: isTotal ? 'FFFFE699' : 'FFF2F2F2' } }
  })
}

function addGroupHeaderRow(ws: ExcelJS.Worksheet, columnCount: number, thicknessKey: string): void {
  const row = ws.addRow([`厚・径: ${thicknessKey}`])
  if (columnCount > 1) {
    ws.mergeCells(row.number, 1, row.number, columnCount)
  }
  const cell = row.getCell(1)
  cell.alignment = leftAlign
  cell.font = { bold: true }
  cell.border = border
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9E1F2' } }
}

function buildShapeSheet(
  wb: ExcelJS.Workbook,
  groupKey: SectionTypeGroupKey,
  items: Item[],
  columnConfigs: ExcelColumn[],
): void {
  const sheetName = sectionTypeGroupLabel[groupKey]
  const ws = wb.addWorksheet(sheetName)
  configurePageSetup(ws)

  const headers = columnConfigs.map(excelColumnLabel)
  const headerRow = ws.addRow([...headers])
  headerRow.eachCell((cell) => {
    cell.alignment = center
    cell.border = border
    cell.font = { bold: true }
  })

  const thicknessGroups = groupByThicknessDiameter(items)
  for (const [thicknessKey, groupItems] of thicknessGroups) {
    addGroupHeaderRow(ws, columnConfigs.length, thicknessKey)
    for (const item of groupItems) {
      const row = ws.addRow(buildItemCells(item, columnConfigs))
      applyDataRowStyle(row, columnConfigs)
    }
    const subtotalRow = ws.addRow(buildAggregationRowCells(groupItems, columnConfigs, '小計'))
    applyAggregationRowStyle(subtotalRow, false)
  }

  const totalRow = ws.addRow(buildAggregationRowCells(items, columnConfigs, '合計'))
  applyAggregationRowStyle(totalRow, true)

  applyColumnWidths(ws, columnConfigs)
}

// ファイル名に使えない記号（\ / : * ? " < > |）
const INVALID_FILE_NAME_SYMBOLS = /[\\/:*?"<>|]/g
// 制御文字（0x00-0x1F）かどうかの判定しきい値
const CONTROL_CHAR_MAX_CODE = 0x1f

// ファイル名に使えない文字（\ / : * ? " < > | と制御文字）を `_` に置換する。
// 発注照合の分割出力（工事名称ごとに複数ファイルを saveAs する）で、工事名称をそのままファイル名へ
// 使うためのサニタイズ（工事名称に `/` 等が含まれるとパス区切りと誤認されファイル保存に失敗するため）。
// 制御文字は正規表現の文字範囲（\x00-\x1F）で書くと no-control-regex に引っかかるため、
// 文字コードでの判定に分けている
export function sanitizeFileNameLabel(label: string): string {
  const withoutControlChars = Array.from(label.trim())
    .map((char) => (char.charCodeAt(0) <= CONTROL_CHAR_MAX_CODE ? '_' : char))
    .join('')
  return withoutControlChars.replace(INVALID_FILE_NAME_SYMBOLS, '_')
}

export async function downloadMillsheetListExcel({
  items,
  constructionName,
  columnConfigs: activeColumnConfigs,
  includeAllEndorsements = false,
  columnLabelOverrides = {},
  fileNameLabel,
}: {
  items: Item[]
  constructionName: string
  columnConfigs: ColumnConfig[] // active なもののみ・出力順に並んでいる想定
  // ホームのデータ出力のみ。ON のとき裏書会社・裏書日を階層6→1 の全列に展開する
  includeAllEndorsements?: boolean
  // 見出しの上書き（ホームのみ。発注照合と共有の columnTypeLabel を変えずに表記を変える）
  columnLabelOverrides?: Partial<Record<ColumnConfig['type'], string>>
  // 発注照合の分割出力用。指定時はファイル名に工事名称を含め、同一秒に複数ファイルを保存しても
  // 名前が衝突しないようにする（他の呼び出し元（ホーム・剪断証明書）は未指定のため挙動は変わらない）
  fileNameLabel?: string
}): Promise<void> {
  // exceljs / file-saver はブラウザ静的 import で問題を起こし得るため、実行時にだけ読む
  const [{ default: ExcelJS }, { saveAs }] = await Promise.all([import('exceljs'), import('file-saver')])
  const wb = new ExcelJS.Workbook()
  const ws = wb.addWorksheet('使用鋼材一覧表')

  configurePageSetup(ws)

  const columnConfigs = buildExcelColumns(activeColumnConfigs, includeAllEndorsements, columnLabelOverrides)

  // 26 列を超えると 'Z' の次が 'AA' になるため、文字コードからの変換ではなく exceljs の列文字を使う
  const lastColumnLetter = ws.getColumn(columnConfigs.length).letter
  const dateCell = ws.getCell(`${lastColumnLetter}1`)
  dateCell.value = dayjs().format('YYYY/M/D')
  dateCell.alignment = { vertical: 'middle', horizontal: 'right' }

  ws.mergeCells(`A2:${lastColumnLetter}2`)
  const titleCell = ws.getCell('A2')
  titleCell.value = '使用鋼材一覧表'
  titleCell.font = { size: 16, bold: true }
  titleCell.alignment = center

  ws.getCell('A3').value = '物件名'
  ws.getCell('B3').value = constructionName
  ws.getCell('A3').alignment = leftAlign
  ws.getCell('B3').alignment = leftAlign
  ws.getCell('G4').alignment = { vertical: 'middle', horizontal: 'right' }

  const headers = columnConfigs.map(excelColumnLabel)
  const headerRow = ws.addRow([...headers])
  headerRow.eachCell((cell) => {
    cell.alignment = center
    cell.border = border
    cell.font = { bold: true }
  })

  const dataRows: Array<Array<string | number | null>> = items.map((item) => buildItemCells(item, columnConfigs))
  const remainingRows = Math.max(0, ITEM_SHOULD_LENGTH - dataRows.length)
  const emptyRows: Array<Array<string | number | null>> = Array.from({ length: remainingRows }, () =>
    columnConfigs.map(() => ''),
  )
  dataRows.push(...emptyRows)
  dataRows.forEach((rowData) => {
    const row = ws.addRow([...rowData])
    applyDataRowStyle(row, columnConfigs)
  })

  applyColumnWidths(ws, columnConfigs)

  const itemsByGroup = items.reduce<Record<SectionTypeGroupKey, Item[]>>(
    (acc, item) => {
      const groupKey = resolveSectionTypeGroup(item.sectionType ?? '')
      acc[groupKey].push(item)
      return acc
    },
    {
      [SECTION_TYPE_GROUPS.PL_FB]: [],
      [SECTION_TYPE_GROUPS.HTB]: [],
      [SECTION_TYPE_GROUPS.PIPE]: [],
      [SECTION_TYPE_GROUPS.SHAPE]: [],
      [SECTION_TYPE_GROUPS.H_BEAM]: [],
      [SECTION_TYPE_GROUPS.OTHER]: [],
    },
  )
  for (const groupKey of SHAPE_SHEET_ORDER) {
    const groupItems = itemsByGroup[groupKey]
    if (groupItems.length === 0) {
      continue
    }
    buildShapeSheet(wb, groupKey, groupItems, columnConfigs)
  }

  const buffer = await wb.xlsx.writeBuffer()
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  // 12時間表記（hh）だと午前/午後で同名ファイルになり得るため24時間表記（HH）にする。
  // 工事名称ごとの分割出力で同一秒の衝突を避ける意図と揃えている（CSV 側の書式と同じ）
  const timestamp = dayjs().format('YYYYMMDDHHmmss')
  const sanitizedLabel = isSome(fileNameLabel) ? sanitizeFileNameLabel(fileNameLabel) : ''
  // 工事名称ごとの分割出力で同一秒に複数ファイルを保存しても名前が衝突しないよう、
  // ラベルが取れたときだけファイル名に含める（未指定時は従来どおりの挙動を維持する）
  const fileName = isPresent(sanitizedLabel)
    ? `使用鋼材一覧表_${sanitizedLabel}_${timestamp}.xlsx`
    : `使用鋼材一覧表_${timestamp}.xlsx`
  saveAs(blob, fileName)
}

// ミルシート（＋その明細）を Excel の行データへ変換する（v1 millsheetsToExcelItems の移植）
export function millsheetsToExcelItems(
  millsheets: readonly MillsheetFieldsFragment[],
  pageNumberByMillsheetId?: ReadonlyMap<string, number | string>,
): Item[] {
  return millsheets.flatMap((millsheet, millsheetIndex) => {
    const pageNumber = isSome(pageNumberByMillsheetId)
      ? (pageNumberByMillsheetId.get(millsheet.id) ?? 'PDFなし')
      : millsheetIndex + 1
    const buildItem = (item?: MillsheetFieldsFragment['millsheetItems'][number]): Item => ({
      steelType: item?.steelType ?? '',
      sectionType: item?.sectionType ?? '',
      size: item?.size ?? '',
      thicknessDiameter: item?.thicknessDiameter ?? null,
      length: item?.length ?? '',
      quantity: item?.quantity.toString() ?? '',
      weight: item?.weightBeforeProcessing?.toString() ?? '',
      heatNumber: item?.heatNumber ?? '',
      individualNumber: item?.individualNumber ?? '',
      no: millsheet.no,
      manufacturerName: millsheet.manufacturerName,
      // 工区・節・部位は明細の属性。明細が無いミルシート行は集約値（全て空）になるため空で出す
      constructionArea: item?.constructionArea ?? '',
      member: item?.member ?? '',
      buildingPart: item?.buildingPart ?? '',
      memo: '',
      pageNumber,
      tradingCompany: millsheet.tradingCompany,
      orderNumber: millsheet.orderNumber,
      innerNo: millsheet.innerNo,
      endorsementCompany: item?.endorsementCompany ?? null,
      endorsementDate: item?.endorsementDate ?? null,
      endorsementCompany2: item?.endorsementCompany2 ?? null,
      endorsementDate2: item?.endorsementDate2 ?? null,
      endorsementCompany3: item?.endorsementCompany3 ?? null,
      endorsementDate3: item?.endorsementDate3 ?? null,
      endorsementCompany4: item?.endorsementCompany4 ?? null,
      endorsementDate4: item?.endorsementDate4 ?? null,
      endorsementCompany5: item?.endorsementCompany5 ?? null,
      endorsementDate5: item?.endorsementDate5 ?? null,
      endorsementCompany6: item?.endorsementCompany6 ?? null,
      endorsementDate6: item?.endorsementDate6 ?? null,
    })
    return millsheet.millsheetItems.length === 0 ? [buildItem()] : millsheet.millsheetItems.map(buildItem)
  })
}

export type MillsheetConstructionGroup<T> = { constructionName: string; millsheets: T[] }

// Object.entries ベースの groupBy では数字だけの工事名称が数値順へ並び替わるため、
// 帳票と Excel の工事順を一致させる目的で最初の出現順を明示的に維持する。
export function groupMillsheetsByConstruction<T extends { constructionName: string }>(
  millsheets: readonly T[],
): Array<MillsheetConstructionGroup<T>> {
  return uniq(millsheets.map((millsheet) => millsheet.constructionName)).map((constructionName) => ({
    constructionName,
    millsheets: millsheets.filter((millsheet) => millsheet.constructionName === constructionName),
  }))
}
